/**
 * Hook pour les appels API Tracking (trips).
 * Utilise le token de la session en cours.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchTrips,
  stopTrip as stopTripApi,
  fetchTripPositions,
  type Trip,
  type TripPosition,
  type FetchTripsParams,
} from "../services/tracking/tripsService";

const TRIPS_KEY = "tracking-trips";

export function useTrackingTrips(params?: FetchTripsParams) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data: trips, error, isLoading, mutate } = useSWR<Trip[]>(
    token ? [TRIPS_KEY, params, token] : null,
    () => fetchTrips(token, params),
    { revalidateOnFocus: false }
  );

  const stopTrip = useCallback(
    async (tripId: string) => {
      const updated = await stopTripApi(token, tripId);
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const refetch = useCallback(() => mutate(), [mutate]);

  return {
    trips: trips ?? [],
    isLoading,
    error,
    refetch,
    stopTrip,
    accessToken: token,
  };
}

export function useTripPositions(tripId: string | null) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data: positions, error, isLoading, mutate } = useSWR<TripPosition[]>(
    token && tripId ? ["tracking-trip-positions", tripId, token] : null,
    () => fetchTripPositions(token, tripId!, { page_size: 500 }),
    { revalidateOnFocus: false }
  );

  return {
    positions: positions ?? [],
    isLoading,
    error,
    refetch: mutate,
  };
}

/**
 * Suivi temps réel d'un trajet via WebSocket.
 * Utilise le consumer /ws/tracking/trips/{trip_id}/ exposé par l'API Tracking.
 */
export function useTripTracking(tripId: string | null) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;
  const [lastPosition, setLastPosition] = useState<TripPosition | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsUrl = useMemo(() => {
    if (!tripId || !token) return null;
    const apiBase = process.env.REACT_APP_TRACKING_WS_URL || process.env.REACT_APP_TRACKING_API_BASE_URL || "";
    if (!apiBase) return null;
    let base = apiBase;
    if (base.startsWith("http://")) base = base.replace("http://", "ws://");
    if (base.startsWith("https://")) base = base.replace("https://", "wss://");
    base = base.replace(/\/+$/, "");
    return `${base}/ws/tracking/trips/${tripId}/?token=${encodeURIComponent(token)}`;
  }, [tripId, token]);

  useEffect(() => {
    if (!wsUrl) {
      return;
    }

    let ws: WebSocket | null = null;
    let closedByUser = false;

    try {
      ws = new WebSocket(wsUrl);
    } catch (e: any) {
      setError(e?.message || "Erreur de connexion WebSocket");
      return;
    }

    ws.onopen = () => {
      setConnected(true);
      setError(null);
    };

    ws.onerror = () => {
      setError("Erreur WebSocket");
    };

    ws.onclose = () => {
      setConnected(false);
      if (!closedByUser && !error) {
        setError("Connexion WebSocket fermée");
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!data || typeof data !== "object") return;
        const type = data.type;
        if (type === "position_update") {
          const latitude = Number(data.latitude);
          const longitude = Number(data.longitude);
          if (Number.isNaN(latitude) || Number.isNaN(longitude)) return;
          const position: TripPosition = {
            id: data.position_id || data.id || `${Date.now()}`,
            latitude,
            longitude,
            recorded_at: data.timestamp || data.recorded_at || new Date().toISOString(),
          } as TripPosition;
          setLastPosition(position);
        }
      } catch (e) {
        // Ignore parsing errors
      }
    };

    return () => {
      closedByUser = true;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [wsUrl]);

  return {
    lastPosition,
    connected,
    error,
  };
}

export type { Trip, TripPosition, FetchTripsParams };
